import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type ReportRow = {
  id: string;
  title: string;
  organization: string;
  year: number | null;
  url: string;
  summary_tr: string;
  summary_en: string;
  category: string;
  topics: string[];
  region: string;
  type: string;
  language: string;
  published: boolean;
  sort_order: number;
};

async function assertAdminCtx(supabase: any, userId: string) {
  const { data: roles } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (!(roles ?? []).some((r: { role: string }) => r.role === "admin")) {
    throw new Error("Unauthorized");
  }
}

export const listPublishedReports = createServerFn({ method: "GET" }).handler(
  async (): Promise<ReportRow[]> => {
    const { data, error } = await supabaseAdmin
      .from("reports")
      .select("*")
      .eq("published", true)
      .order("sort_order", { ascending: false })
      .order("year", { ascending: false, nullsFirst: false })
      .order("title", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as ReportRow[];
  },
);

export const listAdminReports = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ReportRow[]> => {
    await assertAdminCtx(context.supabase, context.userId);
    const { data, error } = await supabaseAdmin
      .from("reports")
      .select("*")
      .order("year", { ascending: false, nullsFirst: false })
      .order("title", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as ReportRow[];
  });

const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().min(1).max(400),
  organization: z.string().max(200).default(""),
  year: z.number().int().min(1900).max(2100).nullable().optional(),
  url: z.string().url().max(800),
  summary_tr: z.string().max(2000).default(""),
  summary_en: z.string().max(2000).default(""),
  category: z.string().min(1).max(80),
  topics: z.array(z.string().min(1).max(60)).max(20).default([]),
  region: z.string().max(80).default("global"),
  type: z.string().max(40).default("report"),
  language: z.string().max(10).default("en"),
  published: z.boolean().default(true),
  sort_order: z.number().int().default(0),
});

export const upsertReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => upsertSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdminCtx(context.supabase, context.userId);
    const { id, ...rest } = data;
    if (id) {
      const { error } = await supabaseAdmin.from("reports").update(rest).eq("id", id);
      if (error) throw new Error(error.message);
      return { id };
    }
    const { data: row, error } = await supabaseAdmin
      .from("reports")
      .insert(rest)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdminCtx(context.supabase, context.userId);
    const { error } = await supabaseAdmin.from("reports").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
